import React, { useEffect, useState } from 'react';
import { Box, ChartSpline, Plus, Trash2, X } from 'lucide-react';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import { useToast } from '../../ui/toast/ToastContext';
import { cn } from '../../../utils/classnames';
import { useConfirm } from '../../ui/confirm/ConfirmContext';
import Input from '../../ui/inputs/Input';
import CurrencyInput from '../../ui/inputs/CurrencyInput';
import CustomCheckbox from '../../ui/checkboxes/CustomCheckbox';
import CustomModal from '../CustomModal';
import Button from '../../ui/buttons/Button';
import CustomOutlineButton from '../../ui/buttons/CustomOutlineButton';
import SecondaryButton from '../../ui/buttons/SecondaryButton';
import RetirementProjectionChart from '../../ui/charts/RetirementProjectionChart';
import LoadingSpinner from '../../ui/loading/LoadingSpinner';
import { formatPercentage } from '../../../utils/formatters';
import DreamInsight from '../../ui/labels/DreamInsight';

const RetirementScenariosModal = ({ onClose, clientId, onSuccess, scope, canWrite = true }) => {
  const [data, setData] = useState({
    retirementScenarios: [],
    clientName: '',
    btgPatrimony: 0,
    clientMonthlySavings: 0,
    expectedReturn: 0,
  });
  const [form, setForm] = useState({
    name: '',
    targetAge: 65,
    useBtgPatrimony: true,
    monthlyContribution: 0,
    desiredIncome: 0,
    consumeAll: false,
    inheritanceTarget: 0,
    investedValue: 0,
    priority: false,
  });
  const [activeScenario, setActiveScenario] = useState(null);
  const [hoveredScenario, setHoveredScenario] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [retirementChartData, setRetirementChartData] = useState(null);
  const { addToast } = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    fetchData();
  }, [clientId]);

  const fetchData = (selectLast = false) => {
    fetch(`/app/planner/client/${clientId}/dashboard/dreams/retirement_scenarios?scope=${scope}`)
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);
        setData(camelData);

        setForm((prev) => ({
          ...prev,
          monthlyContribution: camelData.clientMonthlySavings || 0,
          investedValue: camelData.btgPatrimony || 0,
        }));

        const scenarios = camelData.retirementScenarios;
        if (scenarios?.length > 0) {
          const scenario = selectLast ? scenarios[scenarios.length - 1] : scenarios[0];
          setActiveScenario(scenario.id);
          setForm((prev) => ({ ...prev, ...scenario }));
          handleSimulate(scenario.id, true);
        }
      })
      .catch((err) => {
        console.error('Erro ao buscar dados:', err);
        addToast({ type: 'error', message: 'Erro ao carregar dados.' });
      });
  };

  const handleScenarioChange = (id) => {
    setActiveScenario(id);
    const selected = data.retirementScenarios.find((s) => s.id === id);
    if (selected) {
      setForm((prev) => ({
        ...prev,
        ...selected,
      }));
      handleSimulate(id, true);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'EXCLUIR CENÁRIO?',
      description: `Tem certeza que deseja excluir o cenário?`,
      confirmText: 'EXCLUIR',
    });

    if (!ok) return;

    try {
      const res = await fetchWithCsrf(
        `/app/planner/client/${clientId}/dashboard/dreams/retirement_scenarios/${id}?scope=${scope}`,
        {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
          },
        }
      );

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao excluir o veículo');
      }

      addToast({ type: 'success', message: 'Cenário excluído com sucesso!' });
      setActiveScenario(null);
      fetchData(true);
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  const handleCreate = async () => {
    try {
      const res = await fetchWithCsrf(
        `/app/planner/client/${clientId}/dashboard/dreams/retirement_scenarios?scope=${scope}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
          },
          body: JSON.stringify(
            snakecaseKeys({
              name: 'Novo Cenário',
              targetAge: 65,
              useBtgPatrimony: true,
              consumeAll: true,
              monthlyContribution: data.clientMonthlySavings || 0,
              investedValue: data.btgPatrimony || 0,
            })
          ),
        }
      );

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao criar o cenário');
      }

      addToast({ type: 'success', message: 'Cenário criado com sucesso!' });
      fetchData(true);
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (name) => {
    setForm((prev) => {
      if (name === 'consumeAll') {
        return { ...prev, [name]: !prev[name], inheritanceTarget: 0 };
      }

      if (name === 'useBtgPatrimony') {
        return {
          ...prev,
          [name]: !prev[name],
          investedValue: !prev[name] ? data.btgPatrimony || 0 : 0,
        };
      }

      return { ...prev, [name]: !prev[name] };
    });
  };

  const handleSave = async () => {
    if (!activeScenario) return;

    setIsSaving(true);
    try {
      const res = await fetchWithCsrf(
        `/app/planner/client/${clientId}/dashboard/dreams/retirement_scenarios/${activeScenario}?scope=${scope}`,
        {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
          },
          body: JSON.stringify(snakecaseKeys(form)),
        }
      );

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao salvar o cenário');
      }

      addToast({ type: 'success', message: 'Cenário salvo com sucesso!' });
      fetchData();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSimulate = async (id = null, skipToast = false) => {
    setIsLoading(true);
    try {
      const query = id
        ? ''
        : Object.entries(snakecaseKeys(form))
            .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
            .join('&');

      const baseUrl = `/app/planner/client/${clientId}/dashboard/dreams/retirement_scenarios`;
      const url = id
        ? `${baseUrl}/${id}/chart?scope=${scope}`
        : `${baseUrl}/${id}/chart_by_params?scope=${scope}&${query}`;

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao gerar a simulação');
      }

      const json = await res.json();
      const camelData = camelizeKeys(json);

      setRetirementChartData(camelData.retirementChartData);
      console.log('📊 Dados do gráfico recebidos:', camelData.retirementChartData);
      setShowChart(true);

      if (!skipToast) {
        addToast({ type: 'success', message: 'Simulação concluída!' });
      }
    } catch (err) {
      console.error('Erro ao gerar o gráfico:', err);
      addToast({ type: 'error', message: 'Erro ao gerar o gráfico.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CustomModal onClose={onClose}>
      <div className="p-10">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <Box size={24} className="text-tealMedium" strokeWidth={1.25} />
            <span>Cenários para a Independência Financeira</span>
          </div>
          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        <div className="border-t border-b border-bluePale py-4 mb-6">
          <div className="font-jost text-lg text-blueDarker flex justify-between items-center gap-10">
            <div>
              <span className="font-semibold">Cliente:</span> {data.clientName}
            </div>

            <div>
              <span className="font-semibold">Expectativa de retorno real:</span>{' '}
              {formatPercentage(data.expectedReturn)}
            </div>
          </div>
        </div>

        <div className="flex gap-4 justify-center">
          <div className="flex flex-col gap-2">
            {data.retirementScenarios.map((scenario, index) => {
              const isActive = activeScenario === scenario.id;
              const isHovered = hoveredScenario === scenario.id;

              const isCurrentActiveOrHovered = isActive || isHovered;
              const isPrevActiveOrHovered =
                index > 0 &&
                (activeScenario === data.retirementScenarios[index - 1].id ||
                  hoveredScenario === data.retirementScenarios[index - 1].id);

              const shouldRenderHr = index > 0;
              const hrColor = isCurrentActiveOrHovered || isPrevActiveOrHovered ? 'bg-white' : 'bg-blueWhisper';

              return (
                <React.Fragment key={scenario.id}>
                  {shouldRenderHr && <hr className={cn('mx-5 h-px border-0', hrColor)} />}

                  <li
                    className={cn('list-none rounded-xl', isActive ? 'bg-blueExtraUltraLight' : 'hover:bg-gray-100')}
                    onClick={() => handleScenarioChange(scenario.id)}
                    onMouseEnter={() => setHoveredScenario(scenario.id)}
                    onMouseLeave={() => setHoveredScenario(null)}
                  >
                    <div className="flex items-center gap-3 px-5 pt-5 py-4 cursor-pointer text-base font-medium text-blueDarker justify-between">
                      <div className="flex items-center gap-2">
                        <ChartSpline size={18} className="text-tealMedium" strokeWidth={2} />
                        <span>{scenario.name}</span>
                      </div>
                      {canWrite && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(scenario.id);
                          }}
                          title="Excluir"
                          disabled={scope === 'couple'}
                        >
                          <Trash2
                            className={scope === 'couple' ? 'cursor-not-allowed text-gray-400' : 'text-redBase'}
                            size={18}
                          />
                        </button>
                      )}
                    </div>
                  </li>
                </React.Fragment>
              );
            })}
            {canWrite && (
              <div className="flex items-center gap-2 justify-center">
                <Button
                  onClick={() => handleCreate()}
                  disabled={isSaving || isLoading || data.retirementScenarios.length >= 5}
                >
                  CRIAR CENÁRIO
                </Button>
              </div>
            )}
          </div>
          {activeScenario && (
            <div className="flex-1 overflow-y-auto max-h-screen-70 pr-2 border-l border-bluePale pl-4 grid grid-cols-4">
              <Input
                name="name"
                label="Identificação"
                value={form.name}
                onChange={handleChange}
                required
                disabled={isLoading || isSaving || !canWrite}
              />
              <Input
                name="targetAge"
                label="Idade Alvo"
                type="number"
                value={form.targetAge}
                onChange={handleChange}
                required
                disabled={isLoading || isSaving || !canWrite}
              />
              <CurrencyInput
                name="monthlyContribution"
                label="Aporte Mensal"
                type="number"
                value={form.monthlyContribution}
                onChange={handleChange}
                required
                disabled={isLoading || isSaving || !canWrite}
              />
              <CurrencyInput
                name="desiredIncome"
                label="Renda Desejada"
                type="number"
                value={form.desiredIncome}
                onChange={handleChange}
                required
                disabled={isLoading || isSaving || !canWrite}
              />

              <div className="col-span-2">
                <CustomCheckbox
                  name="consumeAll"
                  label="Consumir todo o patrimônio até o fim da vida"
                  checked={form.consumeAll}
                  onChange={() => handleCheckbox('consumeAll')}
                  labelClassName="text-base"
                  disabled={isLoading || isSaving || !canWrite}
                />
              </div>
              <div className="col-span-2">
                <CustomCheckbox
                  name="useBtgPatrimony"
                  // label="Usar patrimônio da carteira BTG como valor investido"
                  label="Usar patrimônio financeiro total como valor investido"
                  checked={form.useBtgPatrimony}
                  onChange={() => handleCheckbox('useBtgPatrimony')}
                  labelClassName="text-base"
                  disabled={isLoading || isSaving || !canWrite}
                />
              </div>

              <div className="col-span-2">
                <CurrencyInput
                  name="inheritanceTarget"
                  label="Meta de Herança"
                  type="number"
                  value={form.consumeAll ? 0 : form.inheritanceTarget}
                  onChange={handleChange}
                  required={!form.consumeAll}
                  disabled={form.consumeAll || isLoading || isSaving || !canWrite}
                />
              </div>
              <div className="col-span-2">
                <CurrencyInput
                  name="investedValue"
                  label="Valor Investido"
                  type="number"
                  value={form.useBtgPatrimony ? data.btgPatrimony || 0 : form.investedValue}
                  onChange={handleChange}
                  required={!form.useBtgPatrimony}
                  disabled={form.useBtgPatrimony || isLoading || isSaving || !canWrite}
                />
              </div>

              <div className="col-span-4">
                <CustomCheckbox
                  name="priority"
                  label="Cenário prioritário"
                  checked={form.priority}
                  onChange={() => handleCheckbox('priority')}
                  labelClassName="text-base"
                  disabled={isLoading || isSaving || !canWrite}
                />
              </div>

              <div className="col-span-4 flex justify-end gap-3 mt-6">
                <SecondaryButton
                  type="button"
                  onClick={() => handleSimulate(activeScenario)}
                  className="bg-blueWhisper hover:bg-bluePale"
                  disabled={isLoading || isSaving}
                >
                  <div className="w-full max-h-96">SIMULAR GRÁFICO</div>
                </SecondaryButton>
                {canWrite && (
                  <Button
                    onClick={handleSave}
                    disabled={!activeScenario || isSaving || isLoading}
                    className="bg-tealBase hover:bg-tealDark text-white font-medium py-2 px-4 rounded"
                  >
                    SALVAR
                  </Button>
                )}
              </div>
              {showChart &&
                (isLoading ? (
                  <div className="col-span-4 flex flex-col justify-center items-center h-96">
                    <div className="flex justify-center items-center h-96">
                      <LoadingSpinner />
                    </div>
                    <div className="flex flex-col"></div>
                  </div>
                ) : (
                  <div className="flex flex-col col-span-4">
                    <RetirementProjectionChart scenarios={retirementChartData} />
                    <div className="flex flex-col gap-6 mt-4">
                      {retirementChartData.map((scenario) => {
                        const { scenarioId, insights } = scenario;

                        const insightTypes = ['ok', 'problem', 'warning'];

                        const hasInsights = insightTypes.some((type) => insights?.[type]?.length > 0);
                        if (!hasInsights) return null;

                        return (
                          <div key={scenarioId} className="flex flex-col gap-2">
                            {insightTypes.map((type) => {
                              const items = insights?.[type] || [];
                              if (items.length === 0) return null;

                              return (
                                <div key={type} className="flex flex-col gap-1 max-w-screen-100">
                                  {items.map((text, idx) => (
                                    <DreamInsight key={`${scenarioId}-${type}-${idx}`} type={type} text={text} />
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default RetirementScenariosModal;
